import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

const NavBar: React.FC = () => {
  const { user, isSuperadmin, logout } = useAuth();
  const navigate = useNavigate();
  const [analyticsOpen, setAnalyticsOpen] = React.useState(false);
  const [camerasOpen, setCamerasOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-40 bg-mc-charcoal text-white px-4 py-3 flex items-center gap-4 shadow border-b border-mc-amber/60">
        <span className="font-bold tracking-wide flex items-center gap-2">
          MatchCoach SuperAdmin
          <span className="inline-block w-2 h-2 rounded-full bg-mc-amber" />
        </span>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6 w-full">
          <NavLink to="/dashboard" className={({ isActive }) => (isActive ? 'text-mc-amber font-semibold' : 'opacity-80 hover:opacity-100')}>Dashboard</NavLink>
          <NavLink to="/workspaces" className={({ isActive }) => (isActive ? 'text-mc-amber font-semibold' : 'opacity-80 hover:opacity-100')}>Workspaces</NavLink>
          <NavLink to="/users" className={({ isActive }) => (isActive ? 'text-mc-amber font-semibold' : 'opacity-80 hover:opacity-100')}>Users</NavLink>
          <div className="relative">
            <button className="opacity-80 hover:opacity-100" onClick={() => { setCamerasOpen(v => !v); setAnalyticsOpen(false); }}>Cameras</button>
            {camerasOpen && (
              <div className="absolute bg-mc-charcoal text-white mt-2 rounded shadow p-2 space-y-2 whitespace-nowrap w-56 leading-tight border border-white/10" onMouseLeave={() => setCamerasOpen(false)}>
                <button className="block text-left w-full hover:underline whitespace-nowrap px-2 py-1" onClick={() => { setCamerasOpen(false); navigate('/cameras/live'); }}>Live Streams</button>
                <button className="block text-left w-full hover:underline whitespace-nowrap px-2 py-1" onClick={() => { setCamerasOpen(false); navigate('/cameras/settings'); }}>Camera Settings</button>
              </div>
            )}
          </div>
          <div className="relative">
            <button className="whitespace-nowrap opacity-80 hover:opacity-100" onClick={() => { setAnalyticsOpen(v => !v); setCamerasOpen(false); }}>Analytics</button>
            {analyticsOpen && (
              <div className="absolute bg-mc-charcoal text-white mt-2 rounded shadow p-2 space-y-2 whitespace-nowrap w-56 leading-tight border border-white/10" onMouseLeave={() => setAnalyticsOpen(false)}>
                <button className="block text-left w-full hover:underline whitespace-nowrap px-2 py-1" onClick={() => { setAnalyticsOpen(false); navigate('/analytics/active'); }}>Active Recordings</button>
                <button className="block text-left w-full hover:underline whitespace-nowrap px-2 py-1" onClick={() => { setAnalyticsOpen(false); navigate('/analytics/costs'); }}>Cost Attrib per Workspace</button>
                <button className="block text-left w-full hover:underline whitespace-nowrap px-2 py-1" onClick={() => { setAnalyticsOpen(false); navigate('/analytics/new-users'); }}>New Users by Month</button>
                <button className="block text-left w-full hover:underline whitespace-nowrap px-2 py-1" onClick={() => { setAnalyticsOpen(false); navigate('/analytics/comments'); }}>Comment Analytics</button>
              </div>
            )}
          </div>
          <span className="ml-auto text-sm opacity-80">
            {user ? `${user.email}${isSuperadmin ? ' (SuperAdmin)' : ''}` : 'Not signed in'}
          </span>
          {user && (
            <button className="text-sm underline hover:text-mc-amber" onClick={logout}>Logout</button>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="ml-auto md:hidden inline-flex items-center justify-center rounded p-2 border border-white/10 hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white/20"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(v => !v)}
        >
          {mobileOpen ? (
            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          ) : (
            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </nav>

      {/* Backdrop */}
      <button
        aria-hidden={!mobileOpen}
        tabIndex={-1}
        onClick={() => setMobileOpen(false)}
        className={`md:hidden fixed inset-0 z-30 bg-black/40 transition-opacity duration-200 ${mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />

      {/* Mobile menu panel */}
      <div
        className={`md:hidden fixed left-0 right-0 top-14 z-40 bg-mc-charcoal text-white border-t border-white/10 px-4 py-3 space-y-2 transition-all duration-200 ${mobileOpen ? 'opacity-100' : '-translate-y-2 opacity-0 pointer-events-none'}`}
        aria-hidden={!mobileOpen}
      >
        <NavLink to="/dashboard" onClick={() => setMobileOpen(false)} className={({ isActive }) => (isActive ? 'block text-mc-amber font-semibold' : 'block opacity-80 hover:opacity-100')}>Dashboard</NavLink>
        <NavLink to="/workspaces" onClick={() => setMobileOpen(false)} className={({ isActive }) => (isActive ? 'block text-mc-amber font-semibold' : 'block opacity-80 hover:opacity-100')}>Workspaces</NavLink>
        <NavLink to="/users" onClick={() => setMobileOpen(false)} className={({ isActive }) => (isActive ? 'block text-mc-amber font-semibold' : 'block opacity-80 hover:opacity-100')}>Users</NavLink>
        <div className="pt-2">
          <div className="text-xs uppercase tracking-wide opacity-60">Cameras</div>
          <button className="block text-left w-full hover:underline px-1 py-1" onClick={() => { setMobileOpen(false); navigate('/cameras/live'); }}>Live Streams</button>
          <button className="block text-left w-full hover:underline px-1 py-1" onClick={() => { setMobileOpen(false); navigate('/cameras/settings'); }}>Camera Settings</button>
        </div>
        <div className="pt-2">
          <div className="text-xs uppercase tracking-wide opacity-60">Analytics</div>
          <button className="block text-left w-full hover:underline px-1 py-1" onClick={() => { setMobileOpen(false); navigate('/analytics/active'); }}>Active Recordings</button>
          <button className="block text-left w-full hover:underline px-1 py-1" onClick={() => { setMobileOpen(false); navigate('/analytics/costs'); }}>Cost Attrib per Workspace</button>
          <button className="block text-left w-full hover:underline px-1 py-1" onClick={() => { setMobileOpen(false); navigate('/analytics/new-users'); }}>New Users by Month</button>
          <button className="block text-left w-full hover:underline px-1 py-1" onClick={() => { setMobileOpen(false); navigate('/analytics/comments'); }}>Comment Analytics</button>
        </div>
        <div className="pt-2 flex items-center justify-between">
          <span className="text-sm opacity-80">
            {user ? `${user.email}${isSuperadmin ? ' (SuperAdmin)' : ''}` : 'Not signed in'}
          </span>
          {user && (
            <button className="text-sm underline hover:text-mc-amber" onClick={() => { setMobileOpen(false); logout(); }}>Logout</button>
          )}
        </div>
      </div>
    </>
  );
};

export default NavBar;


